/**
 * AI Storybook — finding the faces in a parent's photo, in the browser, so
 * they can tap each face and say who it is (docs/storybook-identity-lock.md
 * §0, §8).
 *
 * It borrows ONLY the ChopBox editor's face model: MediaPipe face mesh on
 * TF.js (engine/analysis/faces.ts), the page's one TF.js WebGL backend
 * (tf.ts) and the checked model files from our own origin (fetchModel.ts).
 * Nothing else of ChopBox runs: not the analyzer, and so not its per-device
 * self-test. Nothing on this path calls analytics; the photo, the faces and
 * the device never leave the page.
 *
 * Everything is reached through import() the first time a photo is added, so
 * TF.js (the lazy chopbox-ai chunk) and the ~3.6 MB of model files are never
 * part of the storybook page's own bundle, and a parent who adds no photo
 * downloads none of it.
 *
 * It fails soft, never throws: the build's kill switch (VITE_CHOPBOX_AI=off),
 * no WebGL, a chunk or model file that won't download, or no answer within
 * STORYBOOK_FACE_TIMEOUT_MS all answer { ok: false }, and the parent marks
 * the faces by hand (StorybookPhotoTagger's "Add a face").
 */

import { aiFlagOn } from '@/components/chopbox/ui/state/constants';
import type { StorybookFaceBox } from '@/lib/storybook-api';

/** Faces looked for in one photo (a family photo rarely has more; the cast is at most 3). */
export const STORYBOOK_MAX_FACES = 6;
/** Long side of the copy the model sees (the ChopBox photo work size). */
export const STORYBOOK_FACE_WORK_LONG_SIDE = 1280;
/** Loading everything the first time plus one run, on a slow phone connection. */
export const STORYBOOK_FACE_TIMEOUT_MS = 20_000;

export type StorybookFaceUnavailable = 'flag-off' | 'no-webgl' | 'load-failed' | 'timeout' | 'failed';

export type StorybookFaceResult =
  | { ok: true; faces: StorybookFaceBox[] }
  | { ok: false; reason: StorybookFaceUnavailable };

/** The parts of face-landmarks-detection's Face this module reads (keypoints in work px). */
export interface StorybookFaceLike {
  keypoints?: Array<{ x: number; y: number }>;
  box?: { xMin: number; yMin: number; xMax: number; yMax: number };
}

type ModelInput = HTMLCanvasElement | ImageData;

interface FaceEngine {
  estimate(work: ModelInput, staticImageMode: boolean): Promise<StorybookFaceLike[]>;
  dispose(): void;
}

/** AnalysisError codes (engine/analysis/types.ts) after which this page never tries again. */
const HARD_CODES: Record<string, StorybookFaceUnavailable> = {
  'no-webgl': 'no-webgl',
  blocked: 'load-failed',
  'context-lost': 'failed',
  checksum: 'failed',
  licence: 'failed',
  'not-in-release': 'failed',
};

let engine: Promise<FaceEngine> | null = null;
let hardFailure: StorybookFaceUnavailable | null = null;
let webglProbe: boolean | null = null;
/** One photo at a time on the GPU: a second call waits for the first. */
let queue: Promise<unknown> = Promise.resolve();

/** The build's kill switch, read at call time (the same rule the ChopBox editor uses). */
export function storybookFaceDetectionEnabled(): boolean {
  try {
    return aiFlagOn(import.meta.env?.VITE_CHOPBOX_AI);
  } catch {
    return true;
  }
}

/**
 * A cheap look for WebGL before downloading 1.3 MB of library: the probe's
 * context is handed straight back so it doesn't count against the browser's
 * context limit.
 */
function hasWebGl(): boolean {
  if (webglProbe !== null) return webglProbe;
  let ok = false;
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl2') || canvas.getContext('webgl')) as WebGLRenderingContext | null;
    ok = Boolean(gl);
    try {
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      /* nothing to give back */
    }
  } catch {
    ok = false;
  }
  webglProbe = ok;
  return ok;
}

async function loadEngine(): Promise<FaceEngine> {
  const [tfModule, fetchModule, facesModule] = await Promise.all([
    import('@/components/chopbox/engine/analysis/tf'),
    import('@/components/chopbox/engine/analysis/fetchModel'),
    import('@/components/chopbox/engine/analysis/faces'),
  ]);
  const handle = await tfModule.loadTf();
  const [detector, mesh] = await Promise.all([
    fetchModule.fetchModel('face-detector'),
    fetchModule.fetchModel('face-mesh'),
  ]);
  const graphs = {
    detector: tfModule.modelArtifacts(handle, detector),
    mesh: tfModule.modelArtifacts(handle, mesh),
  };
  return facesModule.createFaceModel(handle, graphs, STORYBOOK_MAX_FACES);
}

function reasonOf(error: unknown): { reason: StorybookFaceUnavailable; hard: boolean } {
  const code = (error as { name?: string; code?: unknown } | null)?.name === 'AnalysisError'
    ? String((error as { code?: unknown }).code)
    : '';
  if (code in HARD_CODES) return { reason: HARD_CODES[code], hard: true };
  if (code === 'load-failed') return { reason: 'load-failed', hard: false };
  return { reason: 'failed', hard: false };
}

function getEngine(): Promise<FaceEngine> {
  if (!engine) {
    const p = loadEngine();
    engine = p;
    // A download that failed may work on the next photo; a hard failure is remembered in detect().
    p.catch(() => {
      if (engine === p) engine = null;
    });
  }
  return engine;
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('decode_failed'));
    img.src = dataUrl;
  });
}

/**
 * The copy the model sees: at most STORYBOOK_FACE_WORK_LONG_SIDE px on its long
 * side, handed over as ImageData so the model gets these exact bytes (WebKit
 * can upload a lazily drawn canvas blank; ChopBox's analyzer does the same).
 */
function workInput(img: HTMLImageElement): { input: ModelInput; width: number; height: number } {
  const w0 = img.naturalWidth || img.width;
  const h0 = img.naturalHeight || img.height;
  if (!w0 || !h0) throw new Error('empty image');
  const scale = Math.min(1, STORYBOOK_FACE_WORK_LONG_SIDE / Math.max(w0, h0));
  const width = Math.max(1, Math.round(w0 * scale));
  const height = Math.max(1, Math.round(h0 * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(img, 0, 0, width, height);
  try {
    return { input: ctx.getImageData(0, 0, width, height), width, height };
  } catch {
    return { input: canvas, width, height };
  }
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * A face's normalized box from its mesh keypoints (brow to chin, cheek to
 * cheek), or from the detector's own box when no keypoints came back. Null for
 * a degenerate box.
 */
export function faceBoxFromKeypoints(face: StorybookFaceLike, width: number, height: number): StorybookFaceBox | null {
  if (!(width > 0) || !(height > 0)) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const point of face.keypoints || []) {
    if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) continue;
    x0 = Math.min(x0, point.x);
    y0 = Math.min(y0, point.y);
    x1 = Math.max(x1, point.x);
    y1 = Math.max(y1, point.y);
  }
  if (!(x1 > x0 && y1 > y0) && face.box) {
    ({ xMin: x0, yMin: y0, xMax: x1, yMax: y1 } = face.box);
  }
  if (!(x1 > x0 && y1 > y0)) return null;
  const box: StorybookFaceBox = [clamp01(x0 / width), clamp01(y0 / height), clamp01(x1 / width), clamp01(y1 / height)];
  return box[2] > box[0] && box[3] > box[1] ? box : null;
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | 'timeout'> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve('timeout'), ms);
    p.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

async function run(dataUrl: string): Promise<StorybookFaceBox[]> {
  const img = await loadImage(dataUrl);
  const model = await getEngine();
  const { input, width, height } = workInput(img);
  let faces = await model.estimate(input, true);
  // A clear face very occasionally comes back as none on a busy GPU (the
  // ChopBox analyzer asks once more for the same reason).
  if (!faces.length) faces = await model.estimate(input, true);
  return faces
    .map((face) => faceBoxFromKeypoints(face, width, height))
    .filter((box): box is StorybookFaceBox => box !== null)
    .sort((a, b) => a[0] - b[0]);
}

/**
 * Faces in a prepared photo (a JPEG data URL from storybook-photo.ts), as
 * normalized boxes in that photo's pixels, left to right. Never rejects.
 */
export function detectStorybookFaces(dataUrl: string): Promise<StorybookFaceResult> {
  if (!storybookFaceDetectionEnabled()) return Promise.resolve({ ok: false, reason: 'flag-off' });
  if (hardFailure) return Promise.resolve({ ok: false, reason: hardFailure });
  if (!hasWebGl()) {
    hardFailure = 'no-webgl';
    return Promise.resolve({ ok: false, reason: 'no-webgl' });
  }

  const job = queue.then(async (): Promise<StorybookFaceResult> => {
    if (hardFailure) return { ok: false, reason: hardFailure };
    try {
      const faces = await withTimeout(run(dataUrl), STORYBOOK_FACE_TIMEOUT_MS);
      // The load keeps going after a timeout: a later photo can still use it.
      if (faces === 'timeout') return { ok: false, reason: 'timeout' };
      return { ok: true, faces };
    } catch (error) {
      const { reason, hard } = reasonOf(error);
      if (hard) hardFailure = reason;
      // A model that failed mid-run may belong to a lost GPU context: build it again next time.
      if (!hard && reason === 'failed' && engine) {
        const stale = engine;
        engine = null;
        stale.then((m) => m.dispose(), () => undefined);
      }
      return { ok: false, reason };
    }
  });
  queue = job.catch(() => undefined);
  return job;
}

/** Forgets the loaded model and every failure (tests; a page that wants a fresh start). */
export function resetStorybookFaceDetector(): void {
  const stale = engine;
  engine = null;
  hardFailure = null;
  webglProbe = null;
  queue = Promise.resolve();
  stale?.then((m) => m.dispose(), () => undefined);
}
